import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BulkMoveRequest, WorkspaceItem } from '@bower/shared';
import { ActionToast } from '../../components/ui/ActionToast';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { apiService } from '../../services/api.service';
import { patchProject } from '../../services/api/projects.api';
import { bulkMoveWorkspaceItems } from '../../services/api/workspaceItems.api';
import { FOLDER_WORD, getEntityPluralName } from '../../theme/entityIcons';
import { useAppToast } from '../ui/useAppToast';
import { refreshFolderLists } from '../../lib/folderListRefresh';

/** What a move needs to know about an item: a loaded row, or a dragged tree node. */
export type MovableItem = Pick<WorkspaceItem, 'id' | 'kind' | 'parentId' | 'name'>;

/** One item a bulk move actually moved, and what it was called before. */
export interface MovedItem {
  readonly item: MovableItem;
  /** Set only when the move renamed a folder to avoid a clash in the target. */
  readonly originalName?: string;
}

/** The bulk-move request that puts `items` into `folderId` (null = the workspace root). */
export function bulkMoveBody(
  workspaceId: string,
  items: ReadonlyArray<MovableItem>,
  folderId: string | null,
): BulkMoveRequest {
  return {
    items: items.map((item) => ({
      type: item.kind === 'folder' ? 'project' : 'artifact',
      id: item.id,
    })),
    target: folderId ? { type: 'project', id: folderId } : { type: 'workspace', id: workspaceId },
  };
}

interface UndoTally {
  /** Items back under the folder they left. */
  readonly restored: number;
  /** Renamed folders that could not get their old name back. */
  readonly namesKept: number;
}

/** Moved items grouped by the folder they left (null = the root), in first-seen order. */
function groupByParent(moved: ReadonlyArray<MovedItem>): Map<string | null, MovedItem[]> {
  const byParent = new Map<string | null, MovedItem[]>();
  for (const entry of moved) {
    const parent = entry.item.parentId;
    byParent.set(parent, [...(byParent.get(parent) ?? []), entry]);
  }
  return byParent;
}

/** One group home in one all-or-nothing bulk move, then each renamed folder's old name back. */
async function moveGroupBack(
  workspaceId: string,
  parent: string | null,
  group: ReadonlyArray<MovedItem>,
): Promise<UndoTally> {
  const res = await bulkMoveWorkspaceItems(
    apiService,
    bulkMoveBody(
      workspaceId,
      group.map(({ item }) => item),
      parent,
    ),
  ).catch(() => null);
  if (!res?.success) return { restored: 0, namesKept: 0 };
  let namesKept = 0;
  for (const { item, originalName } of group) {
    if (originalName === undefined) continue;
    const renamed = await patchProject(apiService, item.id, 'name', originalName).catch(() => null);
    if (!renamed?.success) namesKept += 1;
  }
  return { restored: group.length, namesKept };
}

function undoToastOptions(total: number, { restored, namesKept }: UndoTally) {
  if (restored === total && namesKept === 0) {
    const noun = total === 1 ? 'item' : 'items';
    return {
      title: 'Undo successful',
      description: `Moved ${total} ${noun} back`,
      status: 'success' as const,
    };
  }
  // The user-visible rename to "folder" is deferred, so this borrows today's
  // words from the entity registry rather than hardcoding either name.
  const collectionPlural = getEntityPluralName('project').toLowerCase();
  const description =
    restored === total
      ? `Moved ${total} back, but ${namesKept} ${namesKept === 1 ? `${FOLDER_WORD} keeps its` : `${collectionPlural} keep their`} new name.`
      : `Moved ${restored} of ${total} items back. The rest stayed where they are.`;
  return { title: 'Undo partially completed', description, status: 'warning' as const };
}

/**
 * The undo toast for a bulk move. Undo is the bulk move again, run backwards:
 * ONE all-or-nothing request per original parent (items grouped by the
 * folder they left), never one request per item. A folder the move renamed
 * to avoid a clash is renamed back once it is home, through the ordinary
 * rename endpoint. Anything that could not be undone is said in the toast.
 */
export function useBulkMoveUndo() {
  const workspaceId = useWorkspaceId() ?? '';
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const undo = useCallback(
    async (moved: ReadonlyArray<MovedItem>) => {
      let tally: UndoTally = { restored: 0, namesKept: 0 };
      for (const [parent, group] of groupByParent(moved)) {
        const done = await moveGroupBack(workspaceId, parent, group);
        tally = {
          restored: tally.restored + done.restored,
          namesKept: tally.namesKept + done.namesKept,
        };
      }
      refreshFolderLists(queryClient, { queryKey: ['workspace-items', { workspaceId }] });
      toast(undoToastOptions(moved.length, tally));
    },
    [queryClient, toast, workspaceId],
  );

  return useCallback(
    (title: string, description: string, moved: ReadonlyArray<MovedItem>) => {
      toast({
        duration: 10000,
        isClosable: true,
        render: ({ onClose }: { onClose: () => void }) => (
          <ActionToast
            title={title}
            description={description}
            status="success"
            actionLabel="Undo"
            onAction={() => void undo(moved)}
            onClose={onClose}
          />
        ),
      });
    },
    [toast, undo],
  );
}
